/**
 * Avatar — Figma `Avatar/24` (6:157) and `Avatar32` (I12:908;6:65), extended by
 * Phase 2.5a lever L4.
 *
 * ===========================================================================
 * WHAT CHANGED FROM THE FIGMA COMPONENT, AND WHY IT IS NOT INVENTION
 * ===========================================================================
 * Figma paints every avatar the same `accent/violet` #8a8cd9 with white
 * initials. Two problems, both measured rather than felt:
 *
 *  1. #ffffff on #8a8cd9 is **3.07:1**. The initials are 12px at weight 500 —
 *     normal-size text — so the shipped component was a WCAG AA failure, in the
 *     same family as the 1.89:1 status pill the phase gate already caught.
 *  2. One colour for everyone carries no information, so the avatar is pure
 *     decoration. L4 asks it to carry identity instead.
 *
 * The eight hue slots in `tokens.css` fix both at once: every slot measures
 * ≥ 4.94:1 in light mode and ≥ 6.98:1 in dark, and the slot is a pure function
 * of who the person is. The change is recorded as an AUTHORED divergence rather
 * than a silent redesign — see PHASE25a and VISUAL-HONESTY.
 *
 * ===========================================================================
 * THE PHOTO SEAM (Phase 2.5b) — BUILT, NOT SHIPPED
 * ===========================================================================
 * Photo upload is explicitly out of scope. What is in scope is making sure the
 * later phase is a data change and not a redesign, so the structure here is
 * already the photo structure: a fixed-size root that owns the shape, the ring
 * and the coloured fill, with the initials as ONE possible child. Dropping a
 * photo in means passing `src` — the disc, the ring, the sizes, the layout and
 * every call site stay exactly as they are, and a photo that 404s falls back to
 * the coloured initials rather than to a broken-image glyph.
 *
 * `src` is therefore live code with no caller today. That is the seam.
 */

import { useState } from 'react';
import { initialsOf } from '../../lib/format';
import { cn } from '../../lib/cn';
import { avatarHueClasses } from './avatar-hue';

/** 24 and 32 are the Figma sizes; 40 is authored for the record-page header. */
export type AvatarSize = 24 | 32 | 40;

export interface AvatarProps {
  /** Display name. Supplies the initials, the hue identity and the a11y label. */
  name?: string;
  /**
   * Pre-computed initials. The legacy call shape — `initials={initialsOf(n)}`.
   * Prefer `name`: two-letter initials are a coarse identity, so "Sam Patel" and
   * "Sara Price" would share a colour if that is all the component is given.
   */
  initials?: string;
  /** The user id — the best hue identity, because it cannot collide. */
  userId?: string | number | null;
  size?: AvatarSize;
  /** Phase 2.5b seam. Falls back to initials if it is empty or fails to load. */
  src?: string | null;
  /** A `bg/surface` ring, for avatars that overlap in a stack. */
  ring?: boolean;
  /**
   * Suppress the a11y label. For the table and topbar, where the person's name
   * is already rendered directly beside the avatar and announcing it twice is
   * noise.
   */
  decorative?: boolean;
  className?: string;
  title?: string;
}

/* 24/32 keep the Figma `Label/12`. 40 moves up to `Body/14`: 12px glyphs in a
 * 40px disc read as a mistake, and both sizes are already in the type ramp, so
 * nothing new is introduced. */
const SIZE_CLASSES: Record<AvatarSize, string> = {
  24: 'size-6 text-label',
  32: 'size-8 text-label',
  40: 'size-10 text-body',
};

export function Avatar({
  name,
  initials,
  userId,
  size = 24,
  src,
  ring = false,
  decorative = false,
  className,
  title,
}: AvatarProps): React.JSX.Element {
  /* Which `src` has already failed, rather than a boolean: a boolean would stay
   * stuck after the prop changed to a different, working URL. */
  const [failedSrc, setFailedSrc] = useState<string | null>(null);

  const glyphs = initials ?? (name === undefined ? '?' : initialsOf(name));

  /* Identity precedence: id beats name beats initials. `String(userId)` is safe
   * for a numeric 0 — it becomes "0", which is a perfectly good identity. */
  const identity =
    userId === null || userId === undefined || userId === ''
      ? (name ?? glyphs)
      : String(userId);

  const showImage = typeof src === 'string' && src !== '' && src !== failedSrc;

  /* A disc reading "?" tells a screen reader nothing, so it is hidden outright
   * rather than announced as an unlabelled image. */
  const label = decorative ? undefined : (title ?? name);

  return (
    <span
      title={title ?? name}
      {...(label === undefined
        ? { 'aria-hidden': true }
        : { role: 'img', 'aria-label': label })}
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full font-medium',
        SIZE_CLASSES[size],
        avatarHueClasses(identity),
        ring && 'ring-2 ring-surface',
        className,
      )}
    >
      {/* The initials always render. When a photo is present it covers them, so
       * a transparent PNG never exposes an empty disc. */}
      <span aria-hidden="true">{glyphs}</span>
      {showImage ? (
        <img
          src={src}
          alt=""
          className="absolute inset-0 size-full rounded-full object-cover"
          onError={() => setFailedSrc(src)}
        />
      ) : null}
    </span>
  );
}
